import type { ProductListItemFragment } from "@/gql/graphql";
import type { ProductCardData } from "./product-card";
import { getColorHex, isColorAttribute, isSizeAttribute } from "@/lib/colors";
import { normalizeSaleorMediaUrl } from "@/lib/media-url";
import { resolveImageAlt } from "@/lib/images/resolve-image-alt";
import { sortSizes } from "@/lib/sizes";
import { defaultLocale, getLocaleConfig } from "@/config/locale";
import { parseProductUxMetadata } from "@/lib/product-ux";
import { metafieldsToEntries, selectCardShortDescription } from "@/lib/product-card-metadata";
import { hasDiscountInPriceRange } from "@/lib/pricing";
import { resolveFrenchProductSummary } from "@/lib/products/french-product-content";
import { getLocalizedCategoryName } from "@/lib/category/localized-name";

/**
 * Extract colors from product variants
 */
function extractColorsFromVariants(
	variants: ProductListItemFragment["variants"],
): { name: string; hex: string }[] {
	const colorSet = new Map<string, string>();

	variants?.forEach((variant) => {
		variant.selectionAttributes?.forEach((attr) => {
			if (isColorAttribute(attr.attribute?.slug ?? "")) {
				attr.values?.forEach((value) => {
					const colorName = value.name;
					if (colorName && !colorSet.has(colorName)) {
						const hex = getColorHex(value) ?? "#6b7280"; // Default gray if no match
						colorSet.set(colorName, hex);
					}
				});
			}
		});
	});

	return Array.from(colorSet.entries()).map(([name, hex]) => ({ name, hex }));
}

/**
 * Extract sizes from product variants
 */
function extractSizesFromVariants(variants: ProductListItemFragment["variants"]): string[] {
	const sizeSet = new Set<string>();

	variants?.forEach((variant) => {
		variant.selectionAttributes?.forEach((attr) => {
			if (isSizeAttribute(attr.attribute?.slug ?? "")) {
				attr.values?.forEach((value) => {
					if (value.name) {
						sizeSet.add(value.name);
					}
				});
			}
		});
	});

	// Sort sizes in logical order (S, M, L, XL or numeric)
	return sortSizes(Array.from(sizeSet));
}

/**
 * Transform Saleor product data to ProductCard format
 */
export function transformToProductCard(product: ProductListItemFragment, channel: string): ProductCardData {
	const parts = channel.split("/");
	const marketChannel = parts.pop() || channel;
	const locale = parts[0] || "de";
	const startPrice = product.pricing?.priceRange?.start?.gross;
	const undiscountedStartPrice = product.pricing?.priceRangeUndiscounted?.start?.gross;
	const productUx = parseProductUxMetadata(metafieldsToEntries(product.metafields), marketChannel, locale);
	const frenchContent = resolveFrenchProductSummary(product, locale);

	// Use centralized pricing logic to detect if ANY variant is on sale
	const isSale = hasDiscountInPriceRange(
		product.pricing?.priceRange,
		product.pricing?.priceRangeUndiscounted,
	);

	// Extract colors and sizes from variants
	const colors = extractColorsFromVariants(product.variants);
	const sizes = extractSizesFromVariants(product.variants);

	const localizedCategoryName = product.category ? getLocalizedCategoryName(product.category, locale) : null;

	const cardName = frenchContent?.name || product.translation?.name || product.name;

	return {
		id: product.id,
		name: cardName,
		slug: product.slug,
		brand: localizedCategoryName,
		price: startPrice?.amount ?? 0,
		compareAtPrice: isSale ? undiscountedStartPrice?.amount : null,
		currency: startPrice?.currency ?? getLocaleConfig(defaultLocale).fallbackCurrency,
		image: product.thumbnail?.url ? normalizeSaleorMediaUrl(product.thumbnail.url) : "/placeholder.svg",
		imageAlt: resolveImageAlt(product.thumbnail?.alt, cardName),
		hoverImage: null, // Would need additional media in fragment
		href: `/${channel}/products/${product.slug}`,
		badge: isSale ? "Sale" : null,
		colors,
		sizes,
		category: product.category
			? {
					id: product.category.id,
					name: localizedCategoryName ?? product.category.name,
					slug: product.category.slug,
				}
			: null,
		createdAt: product.created,
		hasVariants: (product.variants?.length ?? 0) > 1,
		// Card subtitle comes from the slim `ux_card_copy_json` key. The full
		// `ux_market_copy_json` that `localizedMarketCopy` reads is deliberately not
		// selected by the list fragment any more — it was 94% of the payload — so
		// this is the only localized source available here. Products whose slim key
		// has not been synced yet fall through to the German-only behaviour that
		// applied before, so a missing key degrades rather than breaks.
		shortDescription:
			frenchContent?.shortDescription ??
			selectCardShortDescription(product.metafields?.ux_card_copy_json, marketChannel, locale) ??
			(locale === "de" ? productUx.shortDescription : null),
		trustBadges: productUx.trustBadges,
	};
}
